import { Position } from "@xyflow/react";
import { CheckCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ContextualTipsTrigger } from "@babackapp/ui/components/contextual-tips";
import type { ActionNodeReturnAutoTreatConfigDto } from "@babackapp/private-api-client/types";
import { BaseHandle } from "../../components/base-handle";
import { BaseNode, BaseNodeContent } from "../../components/base-node";
import { useNodeData } from "../../hooks/use-node-data";

export const RETURN_AUTO_TREAT_NODE_WIDTH = 280;
export const RETURN_AUTO_TREAT_NODE_HEIGHT_ESTIMATION = 120;

type LiveData = {
  actionType: string;
  config: ActionNodeReturnAutoTreatConfigDto;
};

export function ReturnAutoTreatActionNodeEditor({ id }: { id: string }) {
  const { t } = useTranslation("trackoffice");
  const liveData = useNodeData<LiveData>(id);
  const mode =
    (liveData?.actionType === "auto_treat_return"
      ? liveData.config?.mode
      : undefined) ?? "mark_treated";

  return (
    <ContextualTipsTrigger
      tips={[
        {
          id: "workflow-node-return-auto-treat",
          Icon: CheckCheck,
          title: t(
            "workflowsEditor.tips.workflowNodeAutoTreat.title",
            "This is an auto-treatment action node",
          ),
          description: t(
            "workflowsEditor.tips.workflowNodeAutoTreat.desc",
            "This node treats the return automatically: either flag it as treated, or accept every item through the real treatment flow.",
          ),
        },
      ]}
    >
      <BaseNode nodeId={id} style={{ position: "relative" }}>
        <BaseNodeContent>
          <div className="flex flex-col gap-1 w-full">
            <span className="text-xs text-muted-foreground">
              {t("workflowsEditor.returnAutoTreat.cardLabel", "Mode")}
            </span>
            <span className="text-xs mt-1 font-semibold">
              {t(`workflowsEditor.returnAutoTreat.modes.${mode}`)}
            </span>
          </div>
          <BaseHandle type="target" position={Position.Left} />
          <BaseHandle
            type="source"
            position={Position.Right}
            style={{ top: "50%", transform: "translateY(-50%)" }}
          />
        </BaseNodeContent>
      </BaseNode>
    </ContextualTipsTrigger>
  );
}
